import { useState, useMemo, useEffect } from 'react';
import { ExternalLink, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import MarkdownScrollContainer from './MarkdownScrollContainer';
import BroadcastContent from './BroadcastContent';
import { usePlatformBroadcast } from '@/contexts/PlatformBroadcastContext';

/**
 * PlatformBroadcastPopup — modal global de comunicados.
 *
 * Renderiza o popup "da vez" da fila do PlatformBroadcastContext.
 * - popupMode 'mandatory': sem fechar por ESC/overlay; "Entendi" só
 *   habilita após cumprir requireScrollToEnd / requireVideoFinish.
 * - popupMode 'skippable': fecha ao clicar no overlay.
 * - CTA opcional: também registra acknowledge antes de navegar.
 */
export default function PlatformBroadcastPopup() {
    const { current, acknowledge, dismiss } = usePlatformBroadcast();

    const [scrolled, setScrolled] = useState(false);
    const [videoDone, setVideoDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const broadcast = current;
    const isMandatory = broadcast?.popupMode === 'mandatory';
    const isVideo = broadcast?.mediaType === 'video';

    const requireScroll = Boolean(broadcast?.requireScrollToEnd);
    const requireVideo = Boolean(broadcast?.requireVideoFinish);

    // Reseta estado quando muda o comunicado da vez
    useEffect(() => {
        setScrolled(false);
        setVideoDone(false);
        setSubmitting(false);
    }, [broadcast?.id]);

    const canConfirm = useMemo(() => {
        if (requireScroll && !scrolled) return false;
        if (requireVideo && !videoDone) return false;
        return true;
    }, [requireScroll, scrolled, requireVideo, videoDone]);

    if (!broadcast) return null;

    const handleAcknowledge = async () => {
        if (!canConfirm || submitting) return;
        setSubmitting(true);
        await acknowledge(broadcast.id, {
            scrolledToEnd: scrolled,
            videoFinished: videoDone,
        });
    };

    const handleSkip = () => {
        if (isMandatory) return;
        dismiss(broadcast.id);
    };

    const handleCta = async () => {
        const cta = broadcast.ctaButton;
        if (!cta?.url) return;
        if (canConfirm) {
            await acknowledge(broadcast.id, {
                scrolledToEnd: scrolled,
                videoFinished: videoDone,
            });
        }
        if (cta.openInNewTab) {
            window.open(cta.url, '_blank', 'noopener,noreferrer');
        } else {
            window.location.href = cta.url;
        }
    };

    const cta = broadcast.ctaButton;
    const hasMedia = Boolean(broadcast.mediaUrl);
    const hasCta = cta?.label && cta?.url;

    const gatingHint = !canConfirm
        ? (requireScroll && !scrolled ? 'Role até o fim para continuar'
            : requireVideo && !videoDone ? 'Assista ao vídeo até o final para continuar'
            : null)
        : null;

    return (
        // key força re-mount completo quando muda o comunicado
        <div
            key={broadcast.id}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
            onClick={isMandatory ? undefined : handleSkip}
        >
            <div
                className="relative w-full max-w-2xl rounded-2xl bg-background border border-border shadow-2xl flex flex-col overflow-hidden"
                style={{ maxHeight: 'min(90vh, 800px)' }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pb-popup-title"
            >
                {/* ── Mídia (ocupa toda a largura do topo) ── */}
                {hasMedia && (
                    <div className="relative bg-black flex-shrink-0">
                        {isVideo ? (
                            <video
                                src={broadcast.mediaUrl}
                                controls
                                preload="metadata"
                                onEnded={() => setVideoDone(true)}
                                className="w-full max-h-80 object-contain"
                            />
                        ) : (
                            <img
                                src={broadcast.mediaUrl}
                                alt=""
                                className="w-full max-h-80 object-contain"
                                loading="eager"
                            />
                        )}
                    </div>
                )}

                {/* ── Título ── */}
                <div className={cn(
                    "px-6 pt-5 pb-4 flex-shrink-0",
                    hasMedia && "border-t border-border"
                )}>
                    <h2
                        id="pb-popup-title"
                        className="text-lg font-semibold text-foreground-strong leading-snug"
                    >
                        {broadcast.title}
                    </h2>
                </div>

                {/* ── Conteúdo rolável (Markdown ou HTML) ── */}
                <MarkdownScrollContainer
                    onReachEnd={() => setScrolled(true)}
                    resetKey={broadcast.id}
                    className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar px-6 pb-4 min-h-0"
                >
                    <BroadcastContent content={broadcast.contentMd} format={broadcast.contentFormat} />
                </MarkdownScrollContainer>

                {/* ── Rodapé ── */}
                <div className="px-6 py-4 border-t border-border bg-background flex-shrink-0 space-y-3">
                    {/* Dica de gating */}
                    {gatingHint && (
                        <p className="flex items-center gap-1.5 text-xs text-foreground-muted">
                            <ChevronDown className="w-3.5 h-3.5 animate-bounce shrink-0" />
                            {gatingHint}
                        </p>
                    )}

                    {/* Botões */}
                    <div className={cn("flex gap-3", hasCta ? "flex-row items-center justify-end" : "flex-col")}>
                        {hasCta && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleCta}
                                disabled={!canConfirm || submitting}
                                className="gap-1.5 flex-shrink-0"
                            >
                                {cta.label}
                                {cta.openInNewTab && <ExternalLink className="w-3.5 h-3.5" />}
                            </Button>
                        )}
                        <Button
                            type="button"
                            onClick={handleAcknowledge}
                            disabled={!canConfirm || submitting}
                            className={cn(
                                "bg-cyan-600 hover:bg-cyan-700 text-white font-medium",
                                !hasCta && "w-full"
                            )}
                        >
                            {submitting ? 'Registrando…' : 'Entendi'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
